import {
  createMaterialTopTabNavigator,
  MaterialTopTabBarProps,
  MaterialTopTabNavigationEventMap,
  MaterialTopTabNavigationOptions
} from "expo-router/js-top-tabs";
import { TabNavigationState } from "expo-router/react-navigation";
import { Navigator, useSegments } from 'expo-router';
import { withLayoutContext } from 'expo-router/build/layouts/withLayoutContext';
import { useRouteNode } from 'expo-router/build/Route';
import Animated, { useAnimatedStyle, useSharedValue } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CollapsibleContextProvider } from '@/contexts/collapsibleTabViewContext';
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader';
import useCollapsibleScene from '@/hooks/useCollapsibleScene';
import { ParamListBase } from '@/types/navigation';

const { Navigator: Nav } = createMaterialTopTabNavigator();
const MaterialTopTabs = withLayoutContext<
  MaterialTopTabNavigationOptions,
  typeof Nav,
  TabNavigationState<ParamListBase>,
  MaterialTopTabNavigationEventMap
>(Nav);

const TopTabsInternal = MaterialTopTabs;
/**
 * @param param0
 * @returns
 */
export const TopTabsReanimated = ({
  children,
  headerOptions,
  ...props
}: {
  children?: React.ReactNode;
  headerOptions?: { maxHeight?: number; minHeight?: number };
  screenOptions?: MaterialTopTabNavigationOptions;
  tabBar?: ((props: MaterialTopTabBarProps) => React.ReactNode) | undefined;
  header: (animatedOpacityStyle: { opacity: number }) => React.ReactNode;
}) => {
  const { maxHeight = 200, minHeight = 100 } = headerOptions ?? {};
  const topInsets = useSafeAreaInsets().top;
  const minHeightHeader = minHeight + topInsets;

  const segments = useSegments();
  const activeRouteKey = segments[segments.length - 1];

  // Animation
  const scrollOffset = useSharedValue(0);
  const { animatedHeight, progress } = useCollapsibleHeader({
    scrollOffset,
    maxHeaderHeight: maxHeight,
    minHeaderHeight: minHeightHeader
  });

  const animatedHeaderStyle = useAnimatedStyle(() => ({
    height: Math.round(animatedHeight.value),
    overflow: 'hidden'
  }));

  const animatedOpacityStyle = useAnimatedStyle(() => ({
    opacity: progress.value
  }));

  return (
    <CollapsibleContextProvider
      value={{
        activeRouteKey: activeRouteKey,
        scrollOffset: scrollOffset,
        maxHeaderHeight: maxHeight,
        minHeight: minHeightHeader
      }}>
      <Animated.View style={[{ minHeight: minHeightHeader }, animatedHeaderStyle]}>
        {props.header(animatedOpacityStyle)}
      </Animated.View>
      <TopTabsInternal {...props} screenOptions={{ animationEnabled: false, ...props.screenOptions }}>
        {children}
      </TopTabsInternal>
    </CollapsibleContextProvider>
  );
};

TopTabsReanimated.Screen = Navigator.Screen;

/** Get the scroll props for a child route of a collapsible tabs layout route. */
export function useScrollProps() {
  const route = useRouteNode();
  return useCollapsibleScene(route!.route);
}
